/**
 * PoiListView — POI overview with category/class filters + edit/archive.
 *
 * Sprint 3 Phase 3.7 per POI_ARCHITECTURE.md §11.
 *
 * Rendered as a farm sub-tab in MainCanvas.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

export const CATEGORY_META = {
  zufahrt:        { icon: '🚪', color: '#3b82f6' },
  lagerung:       { icon: '📦', color: '#8b5cf6' },
  infrastruktur:  { icon: '⚡', color: '#eab308' },
  logistik:       { icon: '🚚', color: '#06b6d4' },
  hindernisse:    { icon: '⚠️', color: '#ef4444' },
  naturschutz:    { icon: '🌿', color: '#10b981' },
  operativ:       { icon: '🔧', color: '#f97316' },
  sicherheit:     { icon: '🛡️', color: '#dc2626' },
  sonstiges:      { icon: '📍', color: '#6b7280' },
};

export default function PoiListView({ token, onEditPoi }) {
  const { t } = useTranslation();
  const [pois, setPois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCat) params.set('category', filterCat);
      if (filterClass) params.set('poi_class', filterClass);
      if (showArchived) params.set('include_archived', 'true');
      const res = await fetch(`/api/v1/aif/pois?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPois(data.pois || []);
    } catch (e) {
      console.error('[PoiListView] fetch failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [filterCat, filterClass, showArchived, token]);

  const handleArchive = async (poiId) => {
    try {
      await apiClient.delete(`/api/v1/aif/pois/${poiId}`, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      reload();
    } catch { /* ApiError */ }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="poi-list-view">
      {/* Filter bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] flex-shrink-0 flex-wrap">
        <select
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
          className="px-2.5 py-1.5 rounded-lg border border-[var(--aif-border-default)] bg-[var(--aif-bg-elevated)]
                     text-[var(--aif-text-primary)] text-xs"
        >
          <option value="">{t('poi.allCategories', 'Alle Kategorien')}</option>
          {Object.entries(CATEGORY_META).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {t(`poi.cat.${k}`, k)}</option>
          ))}
        </select>

        <select
          value={filterClass}
          onChange={e => setFilterClass(e.target.value)}
          className="px-2.5 py-1.5 rounded-lg border border-[var(--aif-border-default)] bg-[var(--aif-bg-elevated)]
                     text-[var(--aif-text-primary)] text-xs"
        >
          <option value="">{t('poi.allClasses', 'Alle Klassen')}</option>
          <option value="permanent">{t('poi.class.permanent', 'Stammdaten')}</option>
          <option value="operative">{t('poi.class.operative', 'Operativ')}</option>
        </select>

        <label className="flex items-center gap-1.5 text-xs text-[var(--aif-text-muted)]">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={e => setShowArchived(e.target.checked)}
            className="rounded"
          />
          {t('poi.showArchived', 'Archiviert')}
        </label>

        <span className="ml-auto text-xs text-[var(--aif-text-muted)]">
          {pois.length} {t('poi.count', 'POIs')}
        </span>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-5 h-5 border-2 border-[var(--aif-color-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : pois.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-center px-6">
            <div className="text-3xl mb-2">📍</div>
            <p className="text-sm text-[var(--aif-text-muted)]">{t('poi.empty', 'Keine POIs gefunden')}</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--aif-border-default)]">
            {pois.map(poi => {
              const cat = CATEGORY_META[poi.poi_category] || CATEGORY_META.sonstiges;
              return (
                <button
                  key={poi.id}
                  onClick={() => onEditPoi?.(poi)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left
                             hover:bg-[var(--aif-bg-elevated)] transition"
                >
                  {/* Category icon */}
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-base flex-shrink-0"
                    style={{ backgroundColor: `${cat.color}20`, color: cat.color }}
                  >
                    {cat.icon}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[var(--aif-text-primary)] truncate">
                        {poi.name || t(`poi.cat.${poi.poi_category}`, poi.poi_category)}
                      </span>
                      {poi.status === 'archived' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--aif-text-muted)]/10 text-[var(--aif-text-muted)]">
                          {t('poi.archived', 'archiviert')}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--aif-text-muted)] mt-0.5 flex items-center gap-2">
                      <span>{t(`poi.cat.${poi.poi_category}`, poi.poi_category)}</span>
                      <span>·</span>
                      <span>{poi.poi_class === 'permanent' ? t('poi.class.permanent', 'Stammdaten') : t('poi.class.operative', 'Operativ')}</span>
                      {poi.linked_fields?.length > 0 && (
                        <>
                          <span>·</span>
                          <span>{poi.linked_fields.length} {t('poi.fields', 'Felder')}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Archive action */}
                  {poi.status === 'active' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleArchive(poi.id); }}
                      className="text-xs text-[var(--aif-text-muted)] hover:text-red-500 transition px-2 py-1 flex-shrink-0"
                      title={t('poi.archive', 'Archivieren')}
                    >
                      🗑️
                    </button>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
